/**
 * zustand 전역 store (SPEC §4·§6). UI 컴포넌트는 이 store 만 구독한다.
 * - applySnapshot: 변경된 노드·엣지만 새 객체로 교체(얕은 값 비교) → React Flow 재렌더 최소화.
 * - history: 노드별 처리량 최근 60점. 노드 객체와 분리해 두어(history 갱신이 노드 재렌더를 유발하지 않게) selectNodeHistory 로 읽는다.
 * - events: id 로 중복 제거, 최신순, 최대 500건.
 */
import { create } from "zustand";
export const HISTORY_POINTS = 60;
export const MAX_EVENTS = 500;
const EMPTY_SUMMARY = {
    totalThroughput: 0,
    avgLatency: 0,
    errorRate: 0,
    healthy: 0,
    warning: 0,
    error: 0,
    offline: 0,
    consistencyRate: null,
};
const DEFAULT_FILTERS = {
    query: "",
    statuses: [],
    types: [],
    factories: [],
    eventLevels: [],
};
const DEFAULT_UI = {
    sidebarCollapsed: false,
    detailCollapsed: false,
    eventLogCollapsed: false,
    eventLogAutoScroll: true,
};
/** history·lastUpdated 를 제외한 노드 데이터 값 비교 */
const NODE_COMPARE_KEYS = [
    "label",
    "description",
    "nodeType",
    "status",
    "throughput",
    "latency",
    "successRate",
    "retryCount",
    "factoryId",
    "machineId",
];
function shallowEqualRecord(a, b) {
    if (a === b)
        return true;
    if (!a || !b)
        return false;
    const ak = Object.keys(a);
    const bk = Object.keys(b);
    if (ak.length !== bk.length)
        return false;
    for (const k of ak)
        if (a[k] !== b[k])
            return false;
    return true;
}
export function nodeDataEqual(a, b) {
    for (const k of NODE_COMPARE_KEYS)
        if (a[k] !== b[k])
            return false;
    return shallowEqualRecord(a.extra, b.extra);
}
export function edgeDataEqual(a, b) {
    return a.status === b.status && a.throughput === b.throughput && a.latency === b.latency;
}
function summaryEqual(a, b) {
    return (a.totalThroughput === b.totalThroughput &&
        a.avgLatency === b.avgLatency &&
        a.errorRate === b.errorRate &&
        a.healthy === b.healthy &&
        a.warning === b.warning &&
        a.error === b.error &&
        a.offline === b.offline);
}
/** 새 이벤트를 기존 목록에 병합 (id 중복 제거, 최신순, MAX_EVENTS) */
export function mergeEvents(existing, incoming) {
    if (incoming.length === 0)
        return existing;
    const seen = new Set(existing.map((e) => e.id));
    const fresh = incoming.filter((e) => !seen.has(e.id));
    if (fresh.length === 0)
        return existing;
    const merged = [...fresh, ...existing];
    merged.sort((a, b) => (a.timestamp < b.timestamp ? 1 : a.timestamp > b.timestamp ? -1 : 0));
    return merged.slice(0, MAX_EVENTS);
}
export const usePipelineStore = create()((set, get) => ({
    nodes: [],
    edges: [],
    events: [],
    summary: EMPTY_SUMMARY,
    history: {},
    mode: "idle",
    feedStatus: "idle",
    error: null,
    generatedAt: null,
    lastFetchAt: null,
    lastFetchMs: null,
    consecutiveFailures: 0,
    selectedNodeId: null,
    paused: false,
    filters: DEFAULT_FILTERS,
    uiState: DEFAULT_UI,
    focusRequest: null,
    fitViewRequest: 0,
    layoutRequest: 0,
    actions: {
        applySnapshot: (snapshot, meta) => {
            const prev = get();
            const prevNodeById = new Map(prev.nodes.map((n) => [n.id, n]));
            const prevEdgeById = new Map(prev.edges.map((e) => [e.id, e]));
            let nodesChanged = prev.nodes.length !== snapshot.nodes.length;
            const nodes = snapshot.nodes.map((incoming) => {
                const old = prevNodeById.get(incoming.id);
                if (old && nodeDataEqual(old.data, incoming.data)) {
                    if (old.data.lastUpdated === incoming.data.lastUpdated)
                        return old;
                    // lastUpdated 만 바뀐 경우 — 상세 패널 표시를 위해 data 만 갱신 (position 등 보존)
                    nodesChanged = true;
                    return { ...old, data: { ...old.data, lastUpdated: incoming.data.lastUpdated } };
                }
                nodesChanged = true;
                // 기존 position(사용자 드래그) 보존
                return old?.position && !incoming.position ? { ...incoming, position: old.position } : incoming;
            });
            let edgesChanged = prev.edges.length !== snapshot.edges.length;
            const edges = snapshot.edges.map((incoming) => {
                const old = prevEdgeById.get(incoming.id);
                if (old && old.source === incoming.source && old.target === incoming.target && edgeDataEqual(old.data, incoming.data)) {
                    return old;
                }
                edgesChanged = true;
                return incoming;
            });
            // history: 노드별 처리량 60점 (모든 노드, 매 스냅샷)
            const t = snapshot.generatedAt;
            const history = { ...prev.history };
            for (const n of snapshot.nodes) {
                const arr = history[n.id] ?? [];
                const last = arr[arr.length - 1];
                if (last && last.t === t)
                    continue;
                const next = arr.length >= HISTORY_POINTS ? arr.slice(arr.length - HISTORY_POINTS + 1) : arr.slice();
                next.push({ t, v: n.data.throughput });
                history[n.id] = next;
            }
            set({
                nodes: nodesChanged ? nodes : prev.nodes,
                edges: edgesChanged ? edges : prev.edges,
                events: mergeEvents(prev.events, snapshot.events),
                summary: summaryEqual(prev.summary, snapshot.summary) ? prev.summary : snapshot.summary,
                history,
                mode: snapshot.mode,
                error: snapshot.mode === "mock-fallback" ? (snapshot.error ?? "DB 연결 실패") : null,
                generatedAt: snapshot.generatedAt,
                lastFetchAt: new Date().toISOString(),
                lastFetchMs: meta?.fetchMs ?? prev.lastFetchMs,
                consecutiveFailures: 0,
                feedStatus: snapshot.mode === "db" ? "live" : "simulating",
            });
        },
        setFeedStatus: (feedStatus, error) => set((s) => ({ feedStatus, error: error === undefined ? s.error : error })),
        recordFailure: (error) => set((s) => ({
            consecutiveFailures: s.consecutiveFailures + 1,
            error,
            feedStatus: "error",
        })),
        selectNode: (selectedNodeId) => set({ selectedNodeId }),
        setPaused: (paused) => set({ paused }),
        togglePaused: () => set((s) => ({ paused: !s.paused })),
        setFilters: (patch) => set((s) => ({ filters: { ...s.filters, ...patch } })),
        resetFilters: () => set({ filters: DEFAULT_FILTERS }),
        setUiState: (patch) => set((s) => ({ uiState: { ...s.uiState, ...patch } })),
        addEvent: (event) => set((s) => ({ events: mergeEvents(s.events, [event]) })),
        clearEvents: () => set({ events: [] }),
        requestFocusNode: (id) => set((s) => ({ selectedNodeId: id, focusRequest: { nodeId: id, seq: (s.focusRequest?.seq ?? 0) + 1 } })),
        requestFitView: () => set((s) => ({ fitViewRequest: s.fitViewRequest + 1 })),
        reset: () => set({
            nodes: [],
            edges: [],
            events: [],
            summary: EMPTY_SUMMARY,
            history: {},
            mode: "idle",
            feedStatus: "idle",
            error: null,
            generatedAt: null,
            lastFetchAt: null,
            lastFetchMs: null,
            consecutiveFailures: 0,
            selectedNodeId: null,
        }),
        requestAutoLayout: () => set((s) => ({ layoutRequest: s.layoutRequest + 1 })),
        setNodePositions: (positions) => set((s) => {
            let changed = false;
            const nodes = s.nodes.map((n) => {
                const p = positions[n.id];
                if (!p)
                    return n;
                if (n.position && n.position.x === p.x && n.position.y === p.y)
                    return n;
                changed = true;
                return { ...n, position: { x: p.x, y: p.y } };
            });
            return changed ? { nodes } : {};
        }),
    },
}));
// ---------------- 셀렉터 (컴포넌트에서 usePipelineStore(selectX) 로 사용) ----------------
export const selectActions = (s) => s.actions;
export const selectNodes = (s) => s.nodes;
export const selectEdges = (s) => s.edges;
export const selectEvents = (s) => s.events;
export const selectSummary = (s) => s.summary;
export const selectMode = (s) => s.mode;
export const selectPaused = (s) => s.paused;
export const selectFilters = (s) => s.filters;
export const selectUiState = (s) => s.uiState;
export const selectSelectedNodeId = (s) => s.selectedNodeId;
export const selectFeedStatus = (s) => s.feedStatus;
export const selectError = (s) => s.error;
export const selectGeneratedAt = (s) => s.generatedAt;
export const selectSelectedNode = (s) => s.selectedNodeId ? s.nodes.find((n) => n.id === s.selectedNodeId) : undefined;
const EMPTY_HISTORY = [];
export const selectNodeHistory = (id) => (s) => (id ? s.history[id] : undefined) ?? EMPTY_HISTORY;
/** 전체 시스템 상태 (수집기 노드 기준, SPEC §3) */
export const selectSystemStatus = (s) => {
    let worst = "healthy";
    for (const n of s.nodes) {
        if (!n.id.startsWith("collector:"))
            continue;
        if (n.data.status === "error")
            return "error";
        if (n.data.status === "warning" || n.data.status === "recovering")
            worst = "warning";
    }
    return worst;
};
/** 사이드바 목록·캔버스 하이라이트용 필터 적용 노드 */
export function filterNodes(nodes, f) {
    const q = f.query.trim().toLowerCase();
    return nodes.filter((n) => {
        if (f.statuses.length && !f.statuses.includes(n.data.status))
            return false;
        if (f.types.length && !f.types.includes(n.data.nodeType))
            return false;
        if (f.factories.length && (!n.data.factoryId || !f.factories.includes(n.data.factoryId)))
            return false;
        if (q) {
            const hay = `${n.data.label} ${n.id} ${n.data.machineId ?? ""}`.toLowerCase();
            if (!hay.includes(q))
                return false;
        }
        return true;
    });
}
export function filterEvents(events, f) {
    if (!f.eventLevels.length)
        return events;
    return events.filter((e) => f.eventLevels.includes(e.level));
}
